import { useState, useEffect, useMemo, useCallback } from 'react';
import { useCalculator } from '../../hooks/useCalculator';
import { AddressCard } from './AddressCard';
import { MortgageInputsCard } from './MortgageInputsCard';
import { SpecialProgramsCard } from './SpecialProgramsCard';
import { IncomeCard } from './IncomeCard';
import { PaymentBreakdownCard } from './PaymentBreakdownCard';
import { PMICard } from './PMICard';
import { LoanStatsCard } from './LoanStatsCard';
import { AffordabilityCard } from './AffordabilityCard';
import { CashToCloseCard } from './CashToCloseCard';
import { AmortizationCard } from './AmortizationCard';
import { fmtDollarInt } from '../../lib/format';
import type { CalculatorInputs, CalculatorResults } from '../../lib/types';

export interface ProfileContext {
  name: string;
  cashOnHand: number;
  grossAnnualIncome: number;
  creditScoreRange: string;
  use401k?: boolean;
  loan401kAmount?: number;
}

interface CalculatorProps {
  initialInputs?: Partial<CalculatorInputs>;
  profileContext?: ProfileContext;
  onSaveAnalysis?: (inputs: CalculatorInputs, results: CalculatorResults) => Promise<void>;
  /** Fires on every render with the current live inputs/results — use to read state without saving. */
  onStateChange?: (inputs: CalculatorInputs, results: CalculatorResults) => void;
}

// Key fields that mark a "meaningful" change worth saving
const CHANGE_KEYS: (keyof CalculatorInputs)[] = [
  'homePrice', 'downPaymentPct', 'interestRate', 'loanTerm', 'loanType',
  'grossAnnualIncome', 'monthlyOtherDebt', 'creditScore',
];

export function Calculator({ initialInputs, profileContext, onSaveAnalysis, onStateChange }: CalculatorProps) {
  const { inputs, results, updateInput } = useCalculator(initialInputs);
  const [savedFlash, setSavedFlash] = useState(false);

  // Report live state to parent on every render so parents can read inputs/results
  // without needing to trigger a save first (e.g. for "Plan Offer" in new-analysis mode).
  useEffect(() => {
    onStateChange?.(inputs, results);
  // onStateChange intentionally omitted — we don't want to re-run when the callback
  // reference changes, only when the actual inputs/results values change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs, results]);

  // Input sections default to collapsed on mobile when viewing an existing analysis.
  // On desktop, data-open is ignored by CSS so this has no visual effect.
  const isExistingAnalysis = !!initialInputs;
  const [openSections, setOpenSections] = useState(() => ({
    address: !isExistingAnalysis,
    mortgage: !isExistingAnalysis,
    programs: !isExistingAnalysis,
    income: !isExistingAnalysis,
  }));
  const toggleSection = useCallback((id: keyof typeof openSections) => {
    setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  // Summary strings shown in collapsed card headers (mobile only)
  const addressSummary = useMemo(() => {
    const parts = [inputs.propertyCity, inputs.propertyZip].filter(Boolean);
    return parts.length ? parts.join(' ') : 'No address entered';
  }, [inputs.propertyCity, inputs.propertyZip]);

  const mortgageSummary = useMemo(() => {
    const price = inputs.homePrice >= 1000
      ? `$${Math.round(inputs.homePrice / 1000)}k`
      : fmtDollarInt(inputs.homePrice);
    const loanTypeLabel = inputs.loanType === 'conventional' ? '' : ` ${inputs.loanType.toUpperCase()}`;
    return `${price} · ${inputs.downPaymentPct}% down · ${inputs.interestRate}% · ${inputs.loanTerm}yr${loanTypeLabel}`;
  }, [inputs.homePrice, inputs.downPaymentPct, inputs.interestRate, inputs.loanTerm, inputs.loanType]);

  const programsSummary = useMemo(() => {
    const active: string[] = [];
    if (inputs.use401k) active.push(`401k ${fmtDollarInt(inputs.loan401kAmount)}`);
    if (inputs.useStartUp) active.push(`Start Up ${fmtDollarInt(inputs.startUpLoanAmt)}`);
    if (inputs.useMCC) active.push('MCC');
    return active.length ? active.join(' · ') : 'None active';
  }, [inputs.use401k, inputs.loan401kAmount, inputs.useStartUp, inputs.startUpLoanAmt, inputs.useMCC]);

  const incomeSummary = useMemo(() => {
    const totalDebts = inputs.monthlyCarPayment + inputs.monthlyStudentLoans
      + inputs.monthlyCreditCards + inputs.monthlyOtherDebt;
    const debtPart = totalDebts > 0 ? ` · ${fmtDollarInt(totalDebts)}/mo debts` : '';
    return `${fmtDollarInt(inputs.grossAnnualIncome)}/yr${debtPart}`;
  }, [inputs.grossAnnualIncome, inputs.monthlyCarPayment, inputs.monthlyStudentLoans,
      inputs.monthlyCreditCards, inputs.monthlyOtherDebt]);

  // Detect whether current inputs differ meaningfully from what was loaded
  const hasChanges = useMemo(() => {
    if (!initialInputs || !onSaveAnalysis) return false;
    return CHANGE_KEYS.some(k => initialInputs[k] !== undefined && inputs[k] !== initialInputs[k]);
  }, [inputs, initialInputs, onSaveAnalysis]);

  // Clear the ✓ flash after 3 s
  useEffect(() => {
    if (!savedFlash) return;
    const t = setTimeout(() => setSavedFlash(false), 3000);
    return () => clearTimeout(t);
  }, [savedFlash]);

  // Auto-save 3 s after the last input change when inputs have drifted from initial.
  // onSaveAnalysis/results intentionally omitted to avoid re-triggering after each save.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!onSaveAnalysis || !hasChanges) return;
    const t = setTimeout(() => {
      onSaveAnalysis(inputs, results)
        .then(() => setSavedFlash(true))
        .catch(() => { /* silent */ });
    }, 3000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs, hasChanges]);

  return (
    <div className="container">
      {/* Left column — Inputs */}
      <div className="inputs-col">
        <AddressCard
          inputs={inputs} updateInput={updateInput}
          collapsible={isExistingAnalysis} summary={addressSummary}
          isOpen={openSections.address} onToggle={() => toggleSection('address')}
        />
        <MortgageInputsCard
          inputs={inputs} updateInput={updateInput} loanAmount={results.loanAmount}
          collapsible={isExistingAnalysis} summary={mortgageSummary}
          isOpen={openSections.mortgage} onToggle={() => toggleSection('mortgage')}
        />
        <SpecialProgramsCard
          inputs={inputs} results={results} updateInput={updateInput}
          collapsible={isExistingAnalysis} summary={programsSummary}
          isOpen={openSections.programs} onToggle={() => toggleSection('programs')}
        />
        <IncomeCard
          inputs={inputs} updateInput={updateInput}
          collapsible={isExistingAnalysis} summary={incomeSummary}
          isOpen={openSections.income} onToggle={() => toggleSection('income')}
        />
      </div>

      {/* Auto-save status toast — fixed position, zero layout impact */}
      {onSaveAnalysis && (savedFlash || hasChanges) && (
        <div
          className="autosave-toast"
          style={{
            background: savedFlash ? 'rgba(34,197,94,0.15)' : 'rgba(217,119,6,0.1)',
            border: `1px solid ${savedFlash ? 'rgba(34,197,94,0.4)' : 'rgba(217,119,6,0.35)'}`,
            color: savedFlash ? '#22c55e' : '#d97706',
          }}
        >
          {savedFlash ? '✓ Auto-saved' : '● Modified'}
        </div>
      )}

      {/* Right column — Results */}
      <div className="results-col">
        <PaymentBreakdownCard results={results} />
        <PMICard inputs={inputs} results={results} />
        <AffordabilityCard inputs={inputs} results={results} />
        <CashToCloseCard inputs={inputs} results={results} updateInput={updateInput} cashOnHand={profileContext?.cashOnHand} />
        <LoanStatsCard inputs={inputs} results={results} />
        <AmortizationCard results={results} />
      </div>
    </div>
  );
}
